import { useEffect, useRef } from "react";
import { Item, ItemType } from "@thenounproject/lingo-core";
import { useAppSelectorV1 } from "@redux/hooks";

import KeyCode from "../../constants/keyCodes";
import { getVersionedItemId, scrollItemOnScreen } from "@helpers/items";

const isAssetType = (item: Item) => item.type === ItemType.asset;

type Props = {
  items: Item[];
  selectedIndex: number;
  onSelectItem: (e: KeyboardEvent, id: string, items: Item[]) => void;
  onTogglePreview: (item: Item) => void;
  canEdit: boolean;
};

const useKeyboardHandler = ({
  items,
  selectedIndex,
  onSelectItem,
  onTogglePreview,
  canEdit,
}: Props) => {
  const modals = useAppSelectorV1(state => state.modals);
  const modalIsOpen = Boolean(modals.length);

  const positionCacheRef = useRef<Map<string, DOMRect>>(new Map());
  const cacheTimestampRef = useRef<number>(0);

  // Clear cache when items change
  useEffect(() => {
    positionCacheRef.current.clear();
    cacheTimestampRef.current = 0;
  }, [items]);

  useEffect(() => {
    if (modalIsOpen) return;

    const isValid = (item: Item) => canEdit || isAssetType(item);

    function updatePositionCache() {
      const now = Date.now();
      if (now - cacheTimestampRef.current < 100 && positionCacheRef.current.size > 0) {
        return;
      }

      positionCacheRef.current.clear();
      cacheTimestampRef.current = now;

      items.forEach(item => {
        const el = document.querySelector(`[data-itemid="${item.id}-0"]`) as HTMLElement;
        if (el) {
          positionCacheRef.current.set(item.id, el.getBoundingClientRect());
        }
      });
    }

    function findClosestItemInDirection(
      currentItem: Item,
      direction: "left" | "right" | "up" | "down"
    ): number | null {
      updatePositionCache();

      const currentRect = positionCacheRef.current.get(currentItem.id);
      if (!currentRect) return null;

      const currentCenterX = currentRect.left + currentRect.width / 2;
      const currentCenterY = currentRect.top + currentRect.height / 2;

      // For horizontal navigation, handle wrapping
      if (direction === "left" || direction === "right") {
        const sameRowItems: { index: number; rect: DOMRect; dx: number }[] = [];
        const nextRowItems: { index: number; rect: DOMRect; left: number }[] = [];
        const prevRowItems: { index: number; rect: DOMRect; left: number }[] = [];

        items.forEach((item, index) => {
          if (item.id === currentItem.id || !isValid(item)) return;

          const rect = positionCacheRef.current.get(item.id);
          if (!rect) return;

          const overlapY = rect.top < currentRect.bottom - 5 && rect.bottom > currentRect.top + 5;

          if (overlapY) {
            const dx = rect.left + rect.width / 2 - currentCenterX;
            if (direction === "right" && rect.left > currentRect.right - 10) {
              sameRowItems.push({ index, rect, dx });
            } else if (direction === "left" && rect.right < currentRect.left + 10) {
              sameRowItems.push({ index, rect, dx });
            }
          } else if (rect.top > currentRect.bottom - 5) {
            nextRowItems.push({ index, rect, left: rect.left });
          } else if (rect.bottom < currentRect.top + 5) {
            prevRowItems.push({ index, rect, left: rect.left });
          }
        });

        // If there's an item on the same row in that direction, go there
        if (sameRowItems.length > 0) {
          sameRowItems.sort((a, b) => Math.abs(a.dx) - Math.abs(b.dx));
          return sameRowItems[0]?.index ?? null;
        }

        // Wrap to next/previous row
        if (direction === "right" && nextRowItems.length > 0) {
          nextRowItems.sort((a, b) => a.rect.top - b.rect.top);
          const nearestRowTop = nextRowItems[0].rect.top;
          const nearestRow = nextRowItems.filter(c => Math.abs(c.rect.top - nearestRowTop) < 20);
          nearestRow.sort((a, b) => a.left - b.left);
          return nearestRow[0]?.index ?? null;
        }

        if (direction === "left" && prevRowItems.length > 0) {
          prevRowItems.sort((a, b) => b.rect.bottom - a.rect.bottom);
          const nearestRowBottom = prevRowItems[0].rect.bottom;
          const nearestRow = prevRowItems.filter(
            c => Math.abs(c.rect.bottom - nearestRowBottom) < 20
          );
          nearestRow.sort((a, b) => b.left - a.left);
          return nearestRow[0]?.index ?? null;
        }

        return null;
      }

      // Vertical navigation
      const candidates: { index: number; rect: DOMRect; dx: number; dy: number }[] = [];

      items.forEach((item, index) => {
        if (item.id === currentItem.id || !isValid(item)) return;

        const rect = positionCacheRef.current.get(item.id);
        if (!rect) return;

        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const dx = centerX - currentCenterX;
        const dy = centerY - currentCenterY;

        let isInDirection = false;
        if (direction === "up") {
          isInDirection = rect.bottom < currentRect.top + 10;
        } else if (direction === "down") {
          isInDirection = rect.top > currentRect.bottom - 10;
        }

        if (isInDirection) {
          candidates.push({ index, rect, dx, dy });
        }
      });

      if (candidates.length === 0) return null;

      // Find items in the FIRST row in that direction
      if (direction === "down") {
        candidates.sort((a, b) => a.rect.top - b.rect.top);
      } else {
        candidates.sort((a, b) => b.rect.bottom - a.rect.bottom);
      }

      const firstCandidate = candidates[0];
      const ROW_THRESHOLD = firstCandidate.rect.height * 0.5;

      const targetRowY =
        direction === "down" ? firstCandidate.rect.top : firstCandidate.rect.bottom;

      const nearestRow = candidates.filter(c => {
        const itemY = direction === "down" ? c.rect.top : c.rect.bottom;
        return Math.abs(itemY - targetRowY) < ROW_THRESHOLD;
      });

      // Check if current item is alone on its row (full-width)
      const currentRowItems = Array.from(positionCacheRef.current.entries()).filter(
        ([id, rect]) => {
          if (id === currentItem.id) return false;
          const overlapY = rect.top < currentRect.bottom - 5 && rect.bottom > currentRect.top + 5;
          return overlapY;
        }
      );

      const currentIsFullWidth = currentRowItems.length === 0;

      if (currentIsFullWidth) {
        nearestRow.sort((a, b) => a.rect.left - b.rect.left);
      } else {
        nearestRow.sort((a, b) => Math.abs(a.dx) - Math.abs(b.dx));
      }

      return nearestRow[0]?.index ?? null;
    }

    function detectArrowKeys(e: KeyboardEvent) {
      const inputTargets = ["input", "textarea", "[role=textbox]"];
      if (inputTargets.some(t => (e.target as HTMLElement).matches?.(t))) return;

      const directionMap: Record<string, "left" | "right" | "up" | "down"> = {
        [KeyCode.leftArrow]: "left",
        [KeyCode.rightArrow]: "right",
        [KeyCode.upArrow]: "up",
        [KeyCode.downArrow]: "down",
      };

      const direction = directionMap[e.key];
      if (!direction || selectedIndex == null) return;

      e.preventDefault();

      const currentItem = items[selectedIndex];
      if (!currentItem) return;

      const newIndex = findClosestItemInDirection(currentItem, direction);

      if (newIndex != null && items[newIndex]) {
        const newSelected = items[newIndex];
        onSelectItem(e, getVersionedItemId(newSelected), items);
        scrollItemOnScreen(newSelected.shortId);
      }
    }

    window.addEventListener("keydown", detectArrowKeys);
    return () => {
      window.removeEventListener("keydown", detectArrowKeys);
    };
  }, [items, selectedIndex, onSelectItem, modalIsOpen, onTogglePreview, canEdit]);
};

export default useKeyboardHandler;
